/**
 * RAG Service - Azure AI Search Integration for document knowledge
 * Handles document upload, search, and context building via backend API
 */

import { apiConfig } from "../config/apiConfig.js";
import { getCurrentUserEmail, getApiToken } from "./authService.js";
import { getAttributeLabel } from "../constants/eplanAttributes.js";

const RAG_BASE_URL = apiConfig.baseUrl;

/**
 * Read file content as text
 * @param {File} file - The file to read
 * @returns {Promise<string>} The file content as text
 */
async function readFileContent(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error("Datei konnte nicht gelesen werden"));
        reader.readAsText(file);
    });
}

/**
 * Get file type from file extension
 * @param {File} file - The file
 * @returns {string} The file type
 */
function getFileType(file) {
    const ext = file.name.split('.').pop().toLowerCase();
    const typeMap = {
        'txt': 'text',
        'md': 'markdown',
        'json': 'json',
        'csv': 'csv',
        'pdf': 'pdf',
        'docx': 'docx',
        'xlsx': 'xlsx',
        'epj': 'epj',
        'xml': 'xml'
    };
    return typeMap[ext] || 'text';
}

/**
 * Parse EPJ (EPLAN XML) file and extract O4 nodes from O14 nodes within EplanPxfRoot
 * @param {string} xmlContent - The raw XML content
 * @returns {string} Extracted content prepared for embedding
 */
function parseEpjContent(xmlContent) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
    
    // Check for parse errors
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
        throw new Error('Ungültiges XML-Format in EPJ-Datei');
    }
    
    // Find EplanPxfRoot element
    const eplanPxfRoot = xmlDoc.querySelector('EplanPxfRoot');
    if (!eplanPxfRoot) {
        throw new Error('EplanPxfRoot Element nicht gefunden');
    }
    
    // Find all O14 nodes within EplanPxfRoot
    const o14Nodes = eplanPxfRoot.querySelectorAll('O14');
    if (o14Nodes.length === 0) {
        throw new Error('Keine O14 Elemente in EplanPxfRoot gefunden');
    }
    
    const extractedItems = [];
    
    // Extract O4 nodes from each O14 node
    o14Nodes.forEach((o14Node, o14Index) => {
        const o4Nodes = o14Node.querySelectorAll('O4');
        
        o4Nodes.forEach((o4Node) => {
            const itemData = extractO4NodeData(o4Node, o14Index);
            if (itemData) {
                extractedItems.push(itemData);
            }
        });
    });
    
    if (extractedItems.length === 0) {
        throw new Error('Keine O4 Elemente in O14 Knoten gefunden');
    }
    
    // Format extracted data for embedding
    return formatEpjDataForEmbedding(extractedItems);
}

/**
 * Recursively extract all data from an XML node and its children
 * @param {Element} node - The XML element
 * @param {number} depth - Current recursion depth
 * @returns {Object} Extracted node data with all children
 */
function extractNodeDataRecursive(node, depth = 0) {
    // Extract all attributes
    const attributes = {};
    for (const attr of node.attributes) {
        attributes[attr.name] = attr.value;
    }
    
    // Extract child elements recursively
    const children = [];
    for (const child of node.children) {
        children.push({
            tagName: child.tagName,
            ...extractNodeDataRecursive(child, depth + 1)
        });
    }
    
    // Get direct text content (not from children)
    let textContent = '';
    for (const childNode of node.childNodes) {
        if (childNode.nodeType === Node.TEXT_NODE) {
            const text = childNode.textContent?.trim();
            if (text) {
                textContent += text;
            }
        }
    }
    
    return {
        attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
        textContent: textContent || undefined,
        children: children.length > 0 ? children : undefined
    };
}

/**
 * Extract relevant data from an O4 node (Page) including all children
 * O4 = Page, O17 = Object on Page, S40x1201 contains position (A762 = X/Y)
 * @param {Element} o4Node - The O4 XML element
 * @param {number} o14Index - Index of parent O14 node
 * @returns {Object|null} Extracted data object or null
 */
function extractO4NodeData(o4Node, o14Index) {
    const nodeData = extractNodeDataRecursive(o4Node, 0);
    
    return {
        o14Index,
        tagName: 'O4',
        ...nodeData
    };
}

/**
 * Format extracted EPJ data for embedding
 * @param {Array} items - Array of extracted O4 node data
 * @returns {string} Formatted text for embedding
 */
function formatEpjDataForEmbedding(items) {
    const lines = [
        '=== EPLAN EPJ Projektdaten ===',
        `Anzahl Seiten (O4): ${items.length}`,
        ''
    ];
    
    items.forEach((page, pageIndex) => {
        lines.push(`=== Seite ${pageIndex + 1} (O14 Gruppe: ${page.o14Index + 1}) ===`);
        
        // Add page attributes with labels
        if (page.attributes) {
            lines.push('Seiten-Attribute:');
            for (const [key, value] of Object.entries(page.attributes)) {
                const label = getAttributeLabel(key);
                lines.push(`  ${label}: ${value}`);
            }
        }
        
        // Process children recursively
        if (page.children && page.children.length > 0) {
            formatChildrenForEmbedding(page.children, lines, 1);
        }
        
        lines.push('');
    });
    
    return lines.join('\n');
}

/**
 * Get node type description for EPLAN nodes
 * @param {string} tagName - The XML tag name
 * @returns {string} Human-readable description
 */
function getNodeDescription(tagName) {
    const nodeTypes = {
        'O17': 'Objekt/Symbol',
        'O4': 'Seite',
        'O14': 'Projektstruktur',
        'P11': 'Projekteigenschaften',
        'P49': 'Seiteneigenschaften',
        'P144': 'BMK Hauptfunktion',
        'P150': 'BMK Einbauort',
        'S61x183': 'Objekteigenschaften',
        'S40x1201': 'Positionsdaten',
        'S39x761': 'Transformationsdaten'
    };
    
    // Handle S40x... and S39x... patterns
    if (tagName.startsWith('S40x')) {
        return nodeTypes['S40x1201'] || 'Positionsdaten';
    }
    if (tagName.startsWith('S39x')) {
        return nodeTypes['S39x761'] || 'Transformationsdaten';
    }
    if (tagName.startsWith('S61x')) {
        return nodeTypes['S61x183'] || 'Objekteigenschaften';
    }
    
    return nodeTypes[tagName] || tagName;
}

/**
 * Recursively format child nodes for embedding
 * @param {Array} children - Array of child nodes
 * @param {Array} lines - Output lines array
 * @param {number} depth - Current depth for indentation
 */
function formatChildrenForEmbedding(children, lines, depth) {
    const indent = '  '.repeat(depth);
    
    for (const child of children) {
        const nodeDesc = getNodeDescription(child.tagName);
        
        // Special handling for P144/P150 - combine P20013 + P20014 into BMK ID
        if ((child.tagName === 'P144' || child.tagName === 'P150') && child.attributes) {
            const kennbuchstabe = child.attributes.P20013 || '';
            const zaehlnummer = child.attributes.P20014 || '';
            const bmkId = kennbuchstabe + zaehlnummer;
            
            if (bmkId) {
                lines.push(`${indent}[${child.tagName}] ${nodeDesc}: BMK = "${bmkId}"`);
                // Still show individual attributes for completeness
                if (kennbuchstabe) lines.push(`${indent}  Kennbuchstabe: ${kennbuchstabe}`);
                if (zaehlnummer) lines.push(`${indent}  Zählnummer: ${zaehlnummer}`);
            } else {
                lines.push(`${indent}[${child.tagName}] ${nodeDesc}`);
            }
            
            // Show other attributes besides P20013/P20014
            if (child.attributes) {
                for (const [key, value] of Object.entries(child.attributes)) {
                    if (key !== 'P20013' && key !== 'P20014') {
                        const label = getAttributeLabel(key);
                        lines.push(`${indent}  ${label}: ${value}`);
                    }
                }
            }
        } else {
            lines.push(`${indent}[${child.tagName}] ${nodeDesc}`);
            
            // Add attributes with human-readable labels
            if (child.attributes) {
                for (const [key, value] of Object.entries(child.attributes)) {
                    const label = getAttributeLabel(key);
                    lines.push(`${indent}  ${label}: ${value}`);
                }
            }
        }
        
        // Add text content if present
        if (child.textContent) {
            lines.push(`${indent}  Wert: ${child.textContent}`);
        }
        
        // Recurse into children
        if (child.children && child.children.length > 0) {
            formatChildrenForEmbedding(child.children, lines, depth + 1);
        }
    }
}

/**
 * Check if RAG backend service is available
 */
export async function checkRAGStatus() {
    try {
        const response = await fetch(`${RAG_BASE_URL}/api/rag/status`);
        
        // Check content type before parsing
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            console.warn("RAG status endpoint returned non-JSON response");
            return { available: false, reason: "Service not configured" };
        }
        
        if (!response.ok) {
            return { available: false, reason: "Service unavailable" };
        }
        
        return await response.json();
    } catch (error) {
        console.error("Error checking RAG status:", error);
        return { available: false, reason: error.message };
    }
}

/**
 * Chunk document content into smaller pieces
 * @param {string} content - Document content
 * @param {number} chunkSize - Size of each chunk
 * @param {number} overlap - Overlap between chunks
 * @returns {string[]} Array of chunks
 */
function chunkDocument(content, chunkSize = 3000, overlap = 300) {
    const chunks = [];
    let start = 0;
    
    while (start < content.length) {
        let end = start + chunkSize;
        
        // Try to break at sentence boundary
        if (end < content.length) {
            const lastPeriod = content.lastIndexOf('.', end);
            const lastNewline = content.lastIndexOf('\n', end);
            const breakPoint = Math.max(lastPeriod, lastNewline);
            
            if (breakPoint > start + chunkSize / 2) {
                end = breakPoint + 1;
            }
        }
        
        const chunk = content.substring(start, end).trim();
        if (chunk.length >= 10) {
            chunks.push(chunk);
        }
        
        start = end - overlap;
        if (start < 0) start = 0;
    }
    
    return chunks;
}

/**
 * Upload a document to the RAG system (with local embedding generation)
 * @param {File} file - The file to upload
 * @param {Function} progressCallback - Optional callback for progress updates
 */
export async function uploadDocument(file, progressCallback = null) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) {
        throw new Error("Nicht angemeldet");
    }
    
    // Validate file type (text formats only)
    const allowedTypes = [
        'text/plain',
        'text/csv',
        'application/json',
        'text/markdown',
        'text/xml',
        'application/xml'
    ];
    
    const allowedExtensions = ['.txt', '.csv', '.json', '.md', '.epj', '.xml'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        throw new Error(`Dateityp nicht unterstützt. Erlaubt: ${allowedExtensions.join(', ')}`);
    }
    
    // Check file size - EPJ files skip this check since extraction happens client-side
    // For EPJ files, we only check after extraction; for other files, check now
    const isEpjFile = fileExtension === '.epj';
    if (!isEpjFile) {
        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
            const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
            throw new Error(`Datei ist zu groß: ${fileSizeMB} MB. Maximum: 50 MB`);
        }
    }
    
    if (progressCallback) progressCallback(10, "Datei wird vorbereitet...");
    
    // Read file content as text
    let content = await readFileContent(file);
    let processedFileType = getFileType(file);
    
    // Special handling for EPJ files - extract O4 nodes from O14 within EplanPxfRoot
    if (isEpjFile) {
        if (progressCallback) progressCallback(12, "EPJ-Datei wird analysiert...");
        try {
            const originalSize = content.length;
            content = parseEpjContent(content);
            const extractedSize = content.length;
            const compressionRatio = ((1 - extractedSize / originalSize) * 100).toFixed(1);
            
            console.log(`EPJ Konvertierung: ${(originalSize / 1024).toFixed(1)} KB → ${(extractedSize / 1024).toFixed(1)} KB (${compressionRatio}% reduziert)`);
            
            // Check if extracted content is too large for upload (max 50MB)
            const maxExtractedSize = 50 * 1024 * 1024;
            if (extractedSize > maxExtractedSize) {
                const extractedSizeMB = (extractedSize / 1024 / 1024).toFixed(1);
                throw new Error(`Extrahierte Daten zu groß: ${extractedSizeMB} MB. Maximum: 50 MB`);
            }
            
            processedFileType = 'epj-extracted'; // Mark as processed EPJ
            if (progressCallback) progressCallback(14, `EPJ-Daten extrahiert (${(extractedSize / 1024).toFixed(0)} KB)...`);
        } catch (epjError) {
            throw new Error(`EPJ-Verarbeitung fehlgeschlagen: ${epjError.message}`);
        }
    } else {
        // For non-EPJ files, check content size after reading
        const maxContentSize = 50 * 1024 * 1024;
        if (content.length > maxContentSize) {
            const contentSizeMB = (content.length / 1024 / 1024).toFixed(1);
            throw new Error(`Dateiinhalt zu groß: ${contentSizeMB} MB. Maximum: 50 MB`);
        }
    }
    
    if (progressCallback) progressCallback(15, "Dokument wird vorbereitet...");
    
    // Chunk the document
    const chunks = chunkDocument(content);
    console.log(`Document chunked into ${chunks.length} pieces`);
    
    if (chunks.length === 0) {
        throw new Error('Dokument zu kurz - keine Chunks generiert');
    }
    
    if (progressCallback) progressCallback(20, `Sende ${chunks.length} Chunks zum Backend...`);
    
    try {
        // Upload chunks in batches to backend (backend generates embeddings)
        const batchSize = 100; // Upload 100 chunks at a time
        let uploadedChunks = 0;
        
        for (let i = 0; i < chunks.length; i += batchSize) {
            const batchChunks = chunks.slice(i, Math.min(i + batchSize, chunks.length));
            const isFirstBatch = i === 0;
            const isLastBatch = i + batchSize >= chunks.length;
            
            const percent = 20 + Math.round(((i + batchChunks.length) / chunks.length) * 70); // 20-90%
            if (progressCallback) {
                progressCallback(percent, `Verarbeite Batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(chunks.length / batchSize)}...`);
            }
            
            const response = await fetch(`${RAG_BASE_URL}/api/rag/upload-batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${await getApiToken()}`
                },
                body: JSON.stringify({
                    fileName: file.name,
                    chunks: batchChunks,
                    startIndex: i,
                    totalChunks: chunks.length,
                    fileType: processedFileType,
                    userId: userEmail,
                    isFirstBatch: isFirstBatch,
                    isLastBatch: isLastBatch
                })
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error("Batch upload failed:", response.status, errorText);
                throw new Error(`Batch upload fehlgeschlagen (${response.status})`);
            }
            
            const result = await response.json();
            if (!result.success) {
                throw new Error(result.error || "Batch upload fehlgeschlagen");
            }
            
            uploadedChunks += batchChunks.length;
        }
        
        if (progressCallback) progressCallback(100, "Erfolgreich hochgeladen!");
        
        return {
            success: true,
            chunksUploaded: uploadedChunks,
            fileName: file.name
        };
    } catch (error) {
        if (progressCallback) progressCallback(0, "Fehler beim Upload");
        throw error;
    }
}

/**
 * List all documents for the current user
 */
export async function listDocuments() {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) {
        throw new Error("Nicht angemeldet");
    }
    
    try {
        const response = await fetch(`${RAG_BASE_URL}/api/rag/documents/${encodeURIComponent(userEmail)}`, {
            headers: { Authorization: `Bearer ${await getApiToken()}` }
        });
        
        // Check content type before parsing
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            console.warn("RAG documents endpoint returned non-JSON response");
            return [];
        }
        
        if (!response.ok) {
            if (response.status === 404) {
                return []; // No documents yet
            }
            throw new Error("Fehler beim Laden der Dokumente");
        }
        
        const result = await response.json();
        return result.documents || [];
    } catch (error) {
        console.error("Error listing documents:", error);
        return [];
    }
}

/**
 * Delete a document from the RAG system
 * @param {string} fileName - Name of the file to delete
 */
export async function deleteDocument(fileName) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) {
        throw new Error("Nicht angemeldet");
    }
    
    const response = await fetch(
        `${RAG_BASE_URL}/api/rag/documents/${encodeURIComponent(userEmail)}/${encodeURIComponent(fileName)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${await getApiToken()}` } }
    );
    
    // Check content type before parsing
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Non-JSON response from delete:", text.substring(0, 200));
        throw new Error("RAG-Service nicht verfügbar");
    }
    
    const result = await response.json();
    
    if (!response.ok) {
        throw new Error(result.error || "Löschen fehlgeschlagen");
    }
    
    return result;
}

/**
 * Clear all documents for the current user
 */
export async function clearAllDocuments() {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) {
        throw new Error("Nicht angemeldet");
    }
    
    console.log(`Clearing all documents for user: ${userEmail}`);
    
    const response = await fetch(
        `${RAG_BASE_URL}/api/rag/documents/${encodeURIComponent(userEmail)}/clear-all`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${await getApiToken()}` } }
    );
    
    console.log(`Clear all response status: ${response.status} ${response.statusText}`);
    
    // Check content type before parsing
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Non-JSON response from clear-all:", text.substring(0, 200));
        throw new Error("RAG-Service nicht verfügbar");
    }
    
    const result = await response.json();
    console.log('Clear all response data:', result);
    
    if (!response.ok) {
        console.error('Clear all failed:', result);
        throw new Error(result.error || "Löschen fehlgeschlagen");
    }
    
    return result;
}

/**
 * Search documents for relevant content
 * @param {string} query - Search query
 * @param {number} topK - Number of results to return (default: 5)
 */
export async function searchDocuments(query, topK = 5) {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) {
        throw new Error("Nicht angemeldet");
    }
    
    try {
        const response = await fetch(`${RAG_BASE_URL}/api/rag/search`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await getApiToken()}`
            },
            body: JSON.stringify({
                query,
                userId: userEmail,
                topK
            })
        });
        
        // Check content type before parsing
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            console.warn("RAG search endpoint returned non-JSON response");
            return [];
        }
        
        if (!response.ok) {
            const result = await response.json();
            throw new Error(result.error || "Suche fehlgeschlagen");
        }
        
        const result = await response.json();
        return result.results || [];
    } catch (error) {
        console.error("Error searching documents:", error);
        return [];
    }
}

/**
 * Build context string from Azure AI Search results
 * @param {string} query - The user's query
 * @returns {string|null} - Context string or null if no relevant results
 */
export async function buildAzureSearchContext(query) {
    try {
        const results = await searchDocuments(query, 5);
        
        if (!results || results.length === 0) {
            return null;
        }
        
        // Build context from search results
        const contextParts = results.map((result, index) => {
            const source = result.fileName || result.metadata?.fileName || 'Unbekannt';
            const content = result.content || result.text || '';
            const score = result.score ? ` (Relevanz: ${(result.score * 100).toFixed(0)}%)` : '';
            
            return `[${index + 1}] Quelle: ${source}${score}\n${content}`;
        });
        
        return contextParts.join('\n\n---\n\n');
    } catch (error) {
        console.error("Error building Azure Search context:", error);
        return null;
    }
}

/**
 * Check if Azure AI Search RAG is available and has documents
 */
export async function isAzureSearchAvailable() {
    try {
        const status = await checkRAGStatus();
        if (!status.available) {
            return false;
        }
        
        const documents = await listDocuments();
        return documents.length > 0;
    } catch (error) {
        console.error("Error checking Azure Search availability:", error);
        return false;
    }
}
